import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, DocumentNode } from '@apollo/client';
import {
	ChevronDown,
	Check,
	Loader2,
	X,
	Plus,
	ExternalLink
} from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import {
	EntityDetailsModal,
	supportsEntityDetails
} from './entity-details-modal';

// Generic types for the component
export interface AutocompleteOption {
	id: string;
	displayName: string;
	secondaryText?: string;
	[key: string]: any; // Allow additional properties
}

export interface CreateNewConfig {
	enabled: boolean;
	entityName: string; // e.g., 'Rate', 'Customer', 'Limit'
	createFormComponent: React.ComponentType<{
		onSuccess: (newItem: any) => void;
		onCancel: () => void;
		initialValue?: string;
	}>;
}

type SortDirection = 'ASC' | 'DESC';
type SortInput = {
	[key: string]: SortDirection | SortInput;
};

export interface AutocompleteConfig<T> {
	// GraphQL query configuration
	query: DocumentNode;
	queryName: string; // e.g., 'customers', 'rates', 'limits'

	// Filter and sorting configuration
	searchFields: string[]; // Fields to search in, supports dot paths, e.g., ['name', 'vehicle.plateNumber']
	baseFilters?: Record<string, any>; // Base filters like { isActive: { eq: true } }
	orderBy?: SortInput[]; // Sorting configuration
	extraVariables?: Record<string, any>; // Additional query variables (e.g., { customerId: '...' })

	// Data extraction — override when the connection is nested (e.g., customer.allAssociatedVehicles)
	getConnection?: (data: any) => any; // Extract connection from query result data

	// Display configuration
	getDisplayName: (item: T) => string;
	getSecondaryText?: (item: T) => string;
	getId: (item: T) => string;

	// Entity details configuration (optional)
	enableDetails?: boolean; // Whether to show details info icons
	entityType?: string; // Entity type for details modal (e.g., 'rates', 'customers')

	// Pagination
	pageSize?: number;

	// Create new functionality
	createNew?: CreateNewConfig;
}

export interface GenericAutocompleteProps<T> {
	config: AutocompleteConfig<T>;
	value?: string;
	onChange: (id: string, item: T | null) => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	inputClassName?: string;
	leadingIcon?: React.ReactNode;
	initialSelectedLabel?: string; // Display label to show when form loads with a selected value
	openDelay?: number; // Delay in ms before opening the dropdown
	autoFocus?: boolean; // Automatically focus the input on mount
}

export function GenericAutocomplete<T extends Record<string, any>>({
	config,
	value,
	onChange,
	placeholder = 'Search...',
	disabled = false,
	className = '',
	inputClassName = '',
	leadingIcon,
	initialSelectedLabel,
	openDelay = 0,
	autoFocus = false
}: GenericAutocompleteProps<T>) {
	const { t } = useTranslation('common');
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [userHasTyped, setUserHasTyped] = useState(false); // Track if user has actively typed
	const [selectedItem, setSelectedItem] = useState<T | null>(null);
	const [items, setItems] = useState<T[]>([]);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [endCursor, setEndCursor] = useState<string | null>(null);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [isOpeningModal, setIsOpeningModal] = useState(false); // Temporary flag during modal opening
	const [highlightedIndex, setHighlightedIndex] = useState(-1); // Track keyboard navigation highlight
	const dropdownRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const preventDropdownCloseRef = useRef(false); // Ref to prevent dropdown closing
	const openDelayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const hasOpenedRef = useRef(false); // Track if dropdown has been opened at least once
	const optionRefs = useRef<(HTMLButtonElement | null)[]>([]); // Refs for option buttons to scroll into view
	const scrollContainerRef = useRef<HTMLDivElement>(null); // Ref for scroll container
	const sentinelRef = useRef<HTMLDivElement>(null); // Sentinel element for intersection observer
	const lastFetchedSearchTermRef = useRef<string | null>(null); // Track the last search term we fetched for
	const [isLoadingMore, setIsLoadingMore] = useState(false); // Track infinite scroll loading state

	// Dropdown position for portal rendering
	const [dropdownPosition, setDropdownPosition] = useState({
		top: 0,
		left: 0,
		width: 0,
		openUpwards: false
	});

	// Entity details modal state
	const [detailsModalOpen, setDetailsModalOpen] = useState(false);
	const [detailsEntityId, setDetailsEntityId] = useState<string | null>(null);
	const [detailsEntityName, setDetailsEntityName] = useState<string | null>(
		null
	);

	// Debounced search term
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
		}, 300);
		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Reset pagination when search term changes
	useEffect(() => {
		// Only reset if the search term actually changed from what we last fetched
		if (lastFetchedSearchTermRef.current !== debouncedSearchTerm) {
			setItems([]);
			setHasNextPage(false);
			setEndCursor(null);
		}
	}, [debouncedSearchTerm]);

	// Reset highlighted index when dropdown opens
	useEffect(() => {
		if (isOpen) {
			setHighlightedIndex(-1);
		}
	}, [isOpen]);

	// Scroll highlighted item into view
	useEffect(() => {
		if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
			optionRefs.current[highlightedIndex]?.scrollIntoView({
				block: 'nearest',
				behavior: 'smooth'
			});
		}
	}, [highlightedIndex]);

	// Clean up timeout on unmount
	useEffect(() => {
		return () => {
			if (openDelayTimeoutRef.current) {
				clearTimeout(openDelayTimeoutRef.current);
			}
		};
	}, []);

	// Helper to open dropdown with delay
	const openDropdown = () => {
		if (isOpen) return;

		if (openDelayTimeoutRef.current) {
			clearTimeout(openDelayTimeoutRef.current);
		}

		// Only delay if it hasn't opened yet and delay is configured
		if (openDelay > 0 && !hasOpenedRef.current) {
			openDelayTimeoutRef.current = setTimeout(() => {
				setIsOpen(true);
				hasOpenedRef.current = true;
			}, openDelay);
		} else {
			setIsOpen(true);
			hasOpenedRef.current = true;
		}
	};

	// Build where clause conditionally
	const hasSearchTerm = debouncedSearchTerm.trim().length > 0;
	// Skip query if dropdown is closed OR if we already fetched for this search term
	const alreadyFetchedForSearchTerm =
		lastFetchedSearchTermRef.current === debouncedSearchTerm &&
		items.length > 0;
	const shouldSkipQuery = !isOpen || alreadyFetchedForSearchTerm;
	const shouldFilter = userHasTyped && hasSearchTerm; // Only filter if user has actively typed

	// Build search filters
	const buildSearchFilters = () => {
		if (!shouldFilter) {
			return config.baseFilters ? { and: [config.baseFilters] } : {};
		}

		const buildContainsFilter = (fieldPath: string) => {
			const parts = fieldPath.split('.').filter(Boolean);
			return parts.reduceRight<Record<string, unknown>>(
				(acc, part) => ({ [part]: acc }),
				{ contains: debouncedSearchTerm.trim() }
			);
		};

		const searchFilters = config.searchFields.map(field =>
			buildContainsFilter(field)
		);

		const filters: any[] = [{ or: searchFilters }];

		if (config.baseFilters) {
			filters.push(config.baseFilters);
		}

		return { and: filters };
	};

	const whereClause = buildSearchFilters();

	// Helper to extract connection from query data
	const extractConnection = useCallback(
		(data: any) => {
			if (config.getConnection) return config.getConnection(data);
			return data[config.queryName];
		},
		[config]
	);

	// GraphQL query
	const { data: queryData, loading, error, fetchMore } = useQuery(config.query, {
		variables: {
			where: whereClause,
			first: config.pageSize || 20,
			after: null,
			...(config.orderBy && { order: config.orderBy }),
			...(config.extraVariables || {})
		},
		skip: shouldSkipQuery,
		fetchPolicy: 'cache-and-network' // Always try to fetch fresh data
	});

	// Sync query data into local state (replaces deprecated onCompleted)
	useEffect(() => {
		if (!queryData) return;
		const connection = extractConnection(queryData);
		const edges = connection?.edges || [];
		const newItems = edges.map((edge: any) => edge.node);
		setItems(newItems);
		setHasNextPage(connection?.pageInfo?.hasNextPage || false);
		setEndCursor(connection?.pageInfo?.endCursor || null);
		lastFetchedSearchTermRef.current = debouncedSearchTerm;
	}, [queryData, extractConnection, debouncedSearchTerm]);

	// Load more items (pagination) - used by infinite scroll
	const loadMore = useCallback(async () => {
		if (!hasNextPage || loading || isLoadingMore) return;

		setIsLoadingMore(true);
		try {
			const result = await fetchMore({
				variables: {
					where: whereClause,
					after: endCursor,
					...(config.orderBy && { order: config.orderBy }),
					...(config.extraVariables || {})
				}
			});

			const connection = extractConnection(result.data);
			const edges = connection?.edges || [];
			const newItems = edges.map((edge: any) => edge.node);
			setItems(prev => [...prev, ...newItems]);
			setHasNextPage(connection?.pageInfo?.hasNextPage || false);
			setEndCursor(connection?.pageInfo?.endCursor || null);
		} catch (error) {
			console.error('Error loading more items:', error);
		} finally {
			setIsLoadingMore(false);
		}
	}, [
		hasNextPage,
		loading,
		isLoadingMore,
		fetchMore,
		whereClause,
		endCursor,
		extractConnection
	]);

	// Infinite scroll - detect when user reaches bottom of list
	useEffect(() => {
		if (!isOpen || !sentinelRef.current || !scrollContainerRef.current)
			return;

		const observer = new IntersectionObserver(
			entries => {
				const [entry] = entries;
				if (
					entry.isIntersecting &&
					hasNextPage &&
					!loading &&
					!isLoadingMore
				) {
					loadMore();
				}
			},
			{
				root: scrollContainerRef.current,
				rootMargin: '100px', // Start loading before reaching the very bottom
				threshold: 0
			}
		);

		observer.observe(sentinelRef.current);

		return () => {
			observer.disconnect();
		};
	}, [isOpen, hasNextPage, loading, isLoadingMore, loadMore]);

	// Handle entity details
	const showDetails =
		config.enableDetails &&
		config.entityType &&
		supportsEntityDetails(config.entityType);

	const openDetailsModal = (entityId: string, entityName?: string | null) => {
		// Capture current dropdown state
		const wasDropdownOpen = isOpen;

		// Use ref to immediately prevent any dropdown closing
		preventDropdownCloseRef.current = true;

		// Set opening flag first to prevent dropdown from closing during the process
		setIsOpeningModal(true);

		// Set modal state immediately to prevent any race conditions
		setDetailsModalOpen(true);
		setDetailsEntityId(entityId);
		setDetailsEntityName(entityName ?? null);

		// If dropdown was open, ensure it stays open by re-setting it after a micro-delay
		if (wasDropdownOpen) {
			setTimeout(() => {
				setIsOpen(true);
			}, 0);
		}

		// Clear the opening flag after a short delay, but keep the ref protection longer
		setTimeout(() => {
			setIsOpeningModal(false);
		}, 100);

		// Clear the ref protection after a longer delay to ensure modal is fully rendered
		setTimeout(() => {
			preventDropdownCloseRef.current = false;
		}, 300);
	};

	const handleShowDetails = (item: T, event: React.MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();
		const entityId = config.getId(item);
		const entityName = config.getDisplayName(item);
		openDetailsModal(entityId, entityName);
	};

	const handleShowDetailsFromValue = (event: React.MouseEvent) => {
		if (!value) {
			return;
		}
		event.stopPropagation();
		event.preventDefault();
		const entityName = selectedItem
			? config.getDisplayName(selectedItem)
			: initialSelectedLabel || null;
		openDetailsModal(value, entityName);
	};

	const handleCloseDetails = () => {
		setDetailsModalOpen(false);
		setDetailsEntityId(null);
		setDetailsEntityName(null);
		setIsOpeningModal(false); // Reset opening flag
		preventDropdownCloseRef.current = false; // Reset ref protection
		// Keep dropdown open when closing details modal
		// No need to change isOpen state here
	};

	// Handle click outside to close dropdown
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			// Check ref first for immediate protection
			if (preventDropdownCloseRef.current) {
				return;
			}

			// Don't close dropdown if details modal is open or opening
			if (detailsModalOpen || isOpeningModal) {
				return;
			}

			// Check if click is outside both the input and the dropdown
			const clickedOutsideInput =
				inputRef.current &&
				!inputRef.current.contains(event.target as Node);
			const clickedOutsideDropdown =
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node);

			if (clickedOutsideInput && clickedOutsideDropdown) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () =>
			document.removeEventListener('mousedown', handleClickOutside);
	}, [detailsModalOpen, isOpeningModal]);

	// Handle global ESC key to prevent dropdown from closing when details modal is handling ESC
	useEffect(() => {
		const handleGlobalKeyDown = (event: KeyboardEvent) => {
			if (
				event.key === 'Escape' &&
				(detailsModalOpen || isOpeningModal) &&
				isOpen
			) {
				// Prevent the dropdown's ESC handler from running when details modal is open or opening
				event.stopPropagation();
			}
		};

		if (detailsModalOpen || isOpeningModal) {
			document.addEventListener('keydown', handleGlobalKeyDown, true); // Use capture phase
			return () =>
				document.removeEventListener(
					'keydown',
					handleGlobalKeyDown,
					true
				);
		}
	}, [detailsModalOpen, isOpeningModal, isOpen]);

	// Handle item selection
	const handleItemSelect = (item: T) => {
		setSelectedItem(item);
		setSearchTerm('');
		setUserHasTyped(false); // Reset typed flag when selecting an item
		setIsOpen(false);
		onChange(config.getId(item), item);
	};

	// Clear selection
	const handleClear = () => {
		setSelectedItem(null);
		setSearchTerm('');
		setUserHasTyped(false); // Reset typed flag when clearing
		onChange('', null);
	};

	// Handle successful creation of new item
	const handleCreateSuccess = (newItem: T) => {
		// Close the modal
		setIsCreateModalOpen(false);

		// Add or update the new item in the options list
		setItems(prevItems => {
			const newItemId = config.getId(newItem);
			const filtered = prevItems.filter(
				item => config.getId(item) !== newItemId
			);
			return [newItem, ...filtered];
		});

		// Add a small delay before selection to prevent race conditions
		// This ensures the modal close animation completes and state is stable
		setTimeout(() => {
			handleItemSelect(newItem);
			// Return focus to the autocomplete input so the user can
			// immediately Ctrl+Enter to save the parent form.
			requestAnimationFrame(() => {
				inputRef.current?.focus();
			});
		}, 100);
	};

	// Handle create modal cancel
	const handleCreateCancel = () => {
		setIsCreateModalOpen(false);
		// Return focus to the autocomplete input on cancel too
		requestAnimationFrame(() => {
			inputRef.current?.focus();
		});
	};

	// Get display value for the trigger button
	const getDisplayValue = () => {
		if (selectedItem) {
			return config.getDisplayName(selectedItem);
		}
		// If we have a value but no selectedItem (e.g., on form load), show the initial label
		if (value && initialSelectedLabel) {
			return initialSelectedLabel;
		}
		return placeholder;
	};

	// Effect to sync with external value changes and ensure selected item matches provided id
	useEffect(() => {
		if (!value) {
			if (selectedItem) {
				setSelectedItem(null);
			}
			return;
		}

		const selectedId = selectedItem ? config.getId(selectedItem) : null;

		if (selectedId === value) {
			return;
		}

		const matchingItem = items.find(item => config.getId(item) === value);

		if (matchingItem) {
			setSelectedItem(matchingItem);
		} else if (selectedItem) {
			setSelectedItem(null);
		}
	}, [value, items, selectedItem, config]);

	// Calculate dropdown position when opened with collision detection
	useEffect(() => {
		const updatePosition = () => {
			if (inputRef.current && isOpen) {
				const rect = inputRef.current.getBoundingClientRect();
				const dropdownMaxHeight = 320; // max-h-80 = 20rem = 320px
				const offset = 4; // offset for spacing
				const viewportWidth = window.innerWidth;
				const viewportHeight = window.innerHeight;

				// Vertical collision detection
				const spaceBelow = viewportHeight - rect.bottom;
				const spaceAbove = rect.top;
				const shouldOpenUpwards =
					spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

				// Horizontal collision detection
				let left = rect.left + window.scrollX;
				const dropdownWidth = rect.width;

				// Check if dropdown would overflow on the right
				if (rect.left + dropdownWidth > viewportWidth) {
					// Align to the right edge of the viewport with some padding
					left = viewportWidth - dropdownWidth - 8 + window.scrollX;
				}

				// Check if dropdown would overflow on the left
				if (left < window.scrollX) {
					left = 8 + window.scrollX; // 8px padding from left edge
				}

				setDropdownPosition({
					top: shouldOpenUpwards
						? rect.top + window.scrollY - offset
						: rect.bottom + window.scrollY + offset,
					left: left,
					width: rect.width,
					openUpwards: shouldOpenUpwards
				});
			}
		};

		if (isOpen) {
			// Calculate position immediately on open
			updatePosition();
			window.addEventListener('resize', updatePosition);
			window.addEventListener('scroll', updatePosition, true);
			return () => {
				window.removeEventListener('resize', updatePosition);
				window.removeEventListener('scroll', updatePosition, true);
			};
		} else {
			// Reset position when closed
			setDropdownPosition({
				top: 0,
				left: 0,
				width: 0,
				openUpwards: false
			});
		}
	}, [isOpen]);

	// Handle autoFocus via useEffect for better timing control
	useEffect(() => {
		if (autoFocus && inputRef.current) {
			// Small delay to ensure component is fully mounted
			const timeoutId = setTimeout(() => {
				inputRef.current?.focus();
			}, 0);
			return () => clearTimeout(timeoutId);
		}
	}, [autoFocus]);

	return (
		<div className={`relative ${className}`}>
			{/* Input Field */}
			<div className="relative">
				<input
					ref={inputRef}
					type="text"
					value={isOpen ? searchTerm : getDisplayValue()}
					onChange={e => {
						setSearchTerm(e.target.value);
						setUserHasTyped(true); // Mark that user has actively typed
						if (!isOpen) {
							openDropdown();
						}
						// If user clears the input, clear the selection
						if (e.target.value === '' && selectedItem) {
							handleClear();
						}
					}}
					onFocus={() => {
						if (!disabled) {
							// When focusing, show current value as selected text so user knows what's there
							const currentDisplay = getDisplayValue();
							const displayText = currentDisplay === placeholder ? '' : currentDisplay;
							setSearchTerm(displayText);
							setUserHasTyped(false); // Reset typed flag - just focusing, not typing yet
							openDropdown();
							// Select all text so user can see the current value and type to replace
							requestAnimationFrame(() => {
								inputRef.current?.select();
							});
						}
					}}
					onBlur={() => {
						// Small delay to allow for item selection
						setTimeout(() => {
							// Check ref first for immediate protection
							if (preventDropdownCloseRef.current) {
								return;
							}

							// Don't close dropdown if details modal is open or opening
							if (detailsModalOpen || isOpeningModal) {
								return;
							}

							if (
								!dropdownRef.current?.contains(
									document.activeElement
								)
							) {
								setIsOpen(false);
								// Reset search term to show the selected value
								setSearchTerm('');
								setUserHasTyped(false); // Reset typed flag when closing
							}
						}, 150);
					}}
					onKeyDown={e => {
						// Ctrl+Enter to create new (only when dropdown is open)
						if (
							e.key === 'Enter' &&
							e.ctrlKey &&
							isOpen &&
							config.createNew?.enabled
						) {
							e.preventDefault();
							setIsCreateModalOpen(true);
							setIsOpen(false);
							return;
						}

						if (e.key === 'Escape') {
							// Check ref first for immediate protection
							if (preventDropdownCloseRef.current) {
								return;
							}

							// Don't close dropdown if details modal is open or opening - let the modal handle ESC
							if (detailsModalOpen || isOpeningModal) {
								return;
							}
							setIsOpen(false);
							setSearchTerm('');
							setUserHasTyped(false); // Reset typed flag when closing with Escape
							setHighlightedIndex(-1);
							e.preventDefault();
						} else if (e.key === 'ArrowDown') {
							e.preventDefault();
							if (!isOpen) {
								setSearchTerm('');
								setUserHasTyped(false); // Reset typed flag - just opening, not typing yet
								openDropdown();
							} else if (items.length > 0) {
								// Calculate total options including create new option
								const hasCreateOption =
									config.createNew?.enabled;
								const totalOptions =
									items.length + (hasCreateOption ? 1 : 0);
								setHighlightedIndex(prev =>
									prev < totalOptions - 1 ? prev + 1 : prev
								);
							}
						} else if (e.key === 'ArrowUp') {
							e.preventDefault();
							if (isOpen && items.length > 0) {
								setHighlightedIndex(prev =>
									prev > 0 ? prev - 1 : 0
								);
							}
						} else if (e.key === 'Enter' && isOpen) {
							e.preventDefault();
							if (
								highlightedIndex >= 0 &&
								highlightedIndex < items.length
							) {
								handleItemSelect(items[highlightedIndex]);
							} else if (
								highlightedIndex === items.length &&
								config.createNew?.enabled
							) {
								// Create new option is highlighted
								setIsCreateModalOpen(true);
								setIsOpen(false);
							}
						}
					}}
					placeholder={placeholder}
					disabled={disabled}
					className={`w-full h-9 px-3 py-1 pr-12 bg-[#fafafa] border border-input rounded-md shadow-xs text-base md:text-sm font-semibold placeholder:text-placeholder placeholder:font-light placeholder-shown:font-normal transition-[color,box-shadow] dark:bg-slate-900 ${
						disabled
							? 'opacity-50 cursor-not-allowed'
							: 'hover:border-input focus:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]'
					} ${!selectedItem && !value && !searchTerm && !isOpen ? 'text-muted-foreground' : 'text-foreground'} ${leadingIcon ? 'pl-9' : ''} ${inputClassName}`}
				/>
				{leadingIcon ? (
					<div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
						{leadingIcon}
					</div>
				) : null}
				<div className="absolute flex items-center gap-1 transform -translate-y-1/2 right-1 top-1/2">
					{showDetails && !disabled && (selectedItem || value) && (
						<div
							role="button"
							tabIndex={0}
							onClick={e =>
								selectedItem
									? handleShowDetails(selectedItem, e)
									: handleShowDetailsFromValue(e)
							}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									e.stopPropagation();
									if (selectedItem) {
										// Create a synthetic mouse event for consistency
										const syntheticEvent = {
											...e,
											preventDefault:
												e.preventDefault.bind(e),
											stopPropagation:
												e.stopPropagation.bind(e)
										} as unknown as React.MouseEvent;
										handleShowDetails(
											selectedItem,
											syntheticEvent
										);
									} else {
										const syntheticEvent = {
											...e,
											preventDefault:
												e.preventDefault.bind(e),
											stopPropagation:
												e.stopPropagation.bind(e)
										} as unknown as React.MouseEvent;
										handleShowDetailsFromValue(
											syntheticEvent
										);
									}
								}
							}}
							className="flex items-center justify-center w-6 h-6 transition-colors cursor-pointer text-muted-foreground hover:text-primary"
							title={t('labels.view_details')}
						>
							<ExternalLink className="w-4 h-4 opacity-60 hover:opacity-100" />
						</div>
					)}
					{(selectedItem || (value && initialSelectedLabel)) &&
						!disabled && (
							<div
								role="button"
								tabIndex={0}
								onClick={e => {
									e.stopPropagation();
									handleClear();
								}}
								onKeyDown={e => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										e.stopPropagation();
										handleClear();
									}
								}}
								className="flex items-center justify-center w-6 h-6 transition-colors cursor-pointer text-muted-foreground hover:text-foreground"
								title={t('placeholders.clear_selection')}
							>
								<X className="w-4 h-4" />
							</div>
						)}
					<div
						role="button"
						tabIndex={0}
						onClick={e => {
							e.stopPropagation();
							if (!disabled) {
								if (isOpen) {
									setIsOpen(false);
									setSearchTerm('');
									setUserHasTyped(false); // Reset typed flag when closing
								} else {
									// When opening via dropdown button, start with empty search term to show all options
									setSearchTerm('');
									setUserHasTyped(false); // Reset typed flag - just opening, not typing yet
									openDropdown();
								}
							}
						}}
						onKeyDown={e => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								e.stopPropagation();
								if (!disabled) {
									if (isOpen) {
										setIsOpen(false);
										setSearchTerm('');
										setUserHasTyped(false); // Reset typed flag when closing
									} else {
										setSearchTerm('');
										setUserHasTyped(false); // Reset typed flag - just opening, not typing yet
										openDropdown();
									}
								}
							}
						}}
						className="flex items-center justify-center w-6 h-6 transition-colors cursor-pointer text-muted-foreground hover:text-foreground"
						title={isOpen ? 'Close dropdown' : 'Open dropdown'}
					>
						<ChevronDown
							className={`w-4 h-4 transition-transform duration-200 ${
								isOpen ? 'rotate-180' : ''
							}`}
						/>
					</div>
				</div>
			</div>

			{/* Dropdown Menu - Rendered via Portal */}
			{isOpen &&
				!disabled &&
				dropdownPosition.width > 0 &&
				createPortal(
					<div
						className={`fixed z-50 overflow-hidden duration-200 bg-popover border border-border rounded-md shadow-md max-h-80 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 ${
							dropdownPosition.openUpwards
								? 'origin-bottom'
								: 'origin-top'
						}`}
						style={{
							top: dropdownPosition.openUpwards
								? 'auto'
								: `${dropdownPosition.top}px`,
							bottom: dropdownPosition.openUpwards
								? `${window.innerHeight - dropdownPosition.top}px`
								: 'auto',
							left: `${dropdownPosition.left}px`,
							width: `${dropdownPosition.width}px`,
							pointerEvents: 'auto'
						}}
						ref={dropdownRef}
						onWheel={e => e.stopPropagation()}
						onTouchMove={e => e.stopPropagation()}
						onClick={e => e.stopPropagation()}
						onMouseDown={e => {
							e.stopPropagation();

							// Check if clicking on the scrollbar
							// clientWidth is the inner width (content + padding)
							// offsetWidth is the outer width (content + padding + border + vertical scrollbar)
							// nativeEvent.offsetX is relative to the target element
							const target = e.currentTarget;
							const isScrollbar =
								target.clientWidth < target.offsetWidth &&
								e.nativeEvent.offsetX > target.clientWidth;

							if (!isScrollbar) {
								// If not clicking scrollbar (i.e. clicking items or empty space), prevent default to keep focus on input
								e.preventDefault();
							} else {
								// Clicking scrollbar - allow default (so scrolling works)
								// But we need to prevent the dropdown from closing on blur
								preventDropdownCloseRef.current = true;

								// Reset the ref on mouseup (when drag ends)
								const handleMouseUp = () => {
									preventDropdownCloseRef.current = false;
									document.removeEventListener(
										'mouseup',
										handleMouseUp
									);

									// Optional: try to grab focus back if needed, but usually just preventing close is enough
									// and interacting with scrollbar doesn't necessarily need input focus to be maintaned immediately
								};
								document.addEventListener(
									'mouseup',
									handleMouseUp
								);
							}
						}}
					>
						{/* Options */}
						<div
							ref={scrollContainerRef}
							className="overflow-y-auto max-h-60"
						>
							{loading && items.length === 0 && (
								<div className="flex items-center justify-center px-3 py-6 text-sm text-muted-foreground">
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
									Loading {config.queryName}...
								</div>
							)}

							{error && (
								<div className="px-3 py-6 text-sm text-center text-destructive">
									Error loading {config.queryName}. Please try
									again.
								</div>
							)}

							{!loading &&
								!error &&
								items.length === 0 &&
								shouldFilter && (
									<div className="px-3 py-6 text-sm text-center text-muted-foreground">
										No {config.queryName} found for "
										{debouncedSearchTerm}"
									</div>
								)}

							{!loading &&
								!error &&
								items.length === 0 &&
								!hasSearchTerm && (
									<div className="px-3 py-6 text-sm text-center text-muted-foreground">
										No {config.queryName} found
									</div>
								)}

							{items.map((item, index) => {
								const itemId = config.getId(item);
								const currentSelectedId = selectedItem
									? config.getId(selectedItem)
									: value || '';
								const isSelected = currentSelectedId === itemId;
								const isHighlighted =
									index === highlightedIndex;
								const displayName = config.getDisplayName(item);
								const secondaryText =
									config.getSecondaryText?.(item);

								return (
									<button
										key={itemId}
										ref={el => {
											optionRefs.current[index] = el;
										}}
										onClick={() => handleItemSelect(item)}
										onMouseEnter={() =>
											setHighlightedIndex(index)
										}
										className={`w-full text-left px-3 py-3 text-sm transition-colors duration-150 ${
											isSelected
												? 'bg-accent dark:bg-gray-800 text-accent-foreground border-r-2 border-primary'
												: isHighlighted
													? 'bg-accent/60 text-accent-foreground bg-gray-100 dark:bg-gray-800'
													: 'text-foreground hover:bg-accent/60 hover:text-accent-foreground hover:bg-gray-100 dark:hover:bg-gray-800'
										}`}
									>
										<div className="flex items-center gap-3">
											<div className="flex justify-center flex-shrink-0 w-6">
												{isSelected && (
													<Check className="w-5 h-5 text-primary" />
												)}
											</div>
											<div className="flex-1 min-w-0">
												<div className="font-medium truncate">
													{displayName}
												</div>
												{secondaryText && (
													<div className="text-xs text-muted-foreground truncate mt-0.5">
														{secondaryText}
													</div>
												)}
											</div>
											<div className="flex items-center justify-end gap-2 flex-shrink-0 ml-4 min-w-[2rem]">
												{showDetails && (
													<div
														role="button"
														tabIndex={0}
														onClick={e => {
															e.stopPropagation();
															handleShowDetails(
																item,
																e
															);
														}}
														onKeyDown={e => {
															if (
																e.key ===
																	'Enter' ||
																e.key === ' '
															) {
																e.preventDefault();
																e.stopPropagation();
																const syntheticEvent =
																	{
																		...e,
																		preventDefault:
																			e.preventDefault.bind(
																				e
																			),
																		stopPropagation:
																			e.stopPropagation.bind(
																				e
																			)
																	} as unknown as React.MouseEvent;
																handleShowDetails(
																	item,
																	syntheticEvent
																);
															}
														}}
														className="flex items-center justify-center w-6 h-6 transition-colors cursor-pointer text-muted-foreground hover:text-primary"
														title={t(
															'labels.view_details'
														)}
													>
														<ExternalLink className="w-4 h-4 opacity-60 hover:opacity-100" />
													</div>
												)}
											</div>
										</div>
									</button>
								);
							})}

							{/* Create New Option */}
							{config.createNew?.enabled && (
								<div className="border-t border-border">
									<button
										type="button"
										ref={el => {
											optionRefs.current[items.length] =
												el;
										}}
										onClick={() => {
											setIsCreateModalOpen(true);
											setIsOpen(false);
										}}
										onMouseEnter={() =>
											setHighlightedIndex(items.length)
										}
										className={`w-full px-3 py-3 text-sm font-medium text-left transition-colors duration-150 text-primary ${
											highlightedIndex === items.length
												? 'bg-accent/60 bg-gray-100 dark:bg-gray-800'
												: 'hover:bg-accent/60 hover:text-accent-foreground hover:bg-gray-100 dark:hover:bg-gray-800'
										}`}
									>
										<div className="flex items-center justify-between w-full">
											<div className="flex items-center">
												<Plus className="flex-shrink-0 w-4 h-4 mr-2" />
												<span className="font-medium">
													Create New{' '}
													{
														config.createNew
															.entityName
													}
													{searchTerm.trim() && (
														<span className="ml-1 text-muted-foreground">
															"{searchTerm.trim()}
															"
														</span>
													)}
												</span>
											</div>
											<kbd className="px-1.5 py-0.5 text-xs font-medium text-muted-foreground bg-muted border border-border rounded">
												Ctrl+Enter
											</kbd>
										</div>
									</button>
								</div>
							)}

							{/* Sentinel element for infinite scroll */}
							<div
								ref={sentinelRef}
								className="h-1"
								aria-hidden="true"
							/>

							{/* Loading more indicator */}
							{isLoadingMore && (
								<div className="flex items-center justify-center px-3 py-3 text-sm text-muted-foreground">
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
									Loading more {config.queryName}...
								</div>
							)}
						</div>
					</div>,
					document.body
				)}

			{/* Entity Details Modal */}
			{detailsModalOpen && config.entityType && detailsEntityId && (
				<EntityDetailsModal
					isOpen={detailsModalOpen}
					onClose={handleCloseDetails}
					entityType={config.entityType}
					entityId={detailsEntityId}
					entityDisplayName={detailsEntityName || undefined}
				/>
			)}

			{/* Create New Modal */}
			{config.createNew?.enabled && (
				<Dialog
					open={isCreateModalOpen}
					onOpenChange={setIsCreateModalOpen}
				>
					<DialogContent
						key={`create-${config.createNew.entityName}`}
						className="sm:max-w-2xl sm:max-h-[90vh] overflow-y-auto"
						size="md"
						enablePreRender={true}
						preRenderTimeout={400}
					>
						<DialogHeader>
							<DialogTitle>
								Create New {config.createNew.entityName}
							</DialogTitle>
						</DialogHeader>
						<React.Suspense
							fallback={
								<div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
									Loading {config.createNew.entityName}{' '}
									form...
								</div>
							}
						>
							{React.createElement(
								config.createNew.createFormComponent,
								{
									onSuccess: handleCreateSuccess,
									onCancel: handleCreateCancel,
									initialValue: searchTerm.trim()
								}
							)}
						</React.Suspense>
					</DialogContent>
				</Dialog>
			)}
		</div>
	);
}
